import { Component, ChangeDetectionStrategy, PLATFORM_ID, inject, ViewChildren, QueryList, AfterViewInit, ElementRef, OnDestroy, DestroyRef } from '@angular/core';
import { SectionComponent } from '../../components/section/section.component';
import { CommonModule, isPlatformServer } from '@angular/common';

import { EditorIdDirective } from '../../../app/directives/editor-id.directive';
import { Section, SectionAttributes } from '../../../app/types/section';
import { PlainImageComponent, PlainImageSection } from '../../../app/components/plain-image/plain-image.component';
import { RouterLink } from '@angular/router';
import { startWith } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CardComponent } from '@knorr-bremse-portals/ngx-components/components/card';
import { ChipComponent } from '@knorr-bremse-portals/ngx-components/components/chip';
import { HtmlComponent } from '@knorr-bremse-portals/ngx-components/components/html';
import { ChipSetComponent } from '@knorr-bremse-portals/ngx-components/components/chip-set';
import { FsButtonComponent } from '../../components/fs-button/fs-button.component';

interface TimelinePeriodEditorIds {
  sections?: string;
  fromYear?: string;
  tillYear?: string;
}
interface TimelineCardAttributes extends SectionAttributes {
  copyText: string;
  image?: PlainImageSection;
  headline?: string;
  fromDate?: string;
  tillDate?: string;
}

type TimelineCardEditorIds = [
  'copyText',
  'headline',
  'fromDate',
  'tillDate',
];

type TimelineCardSection = Section<TimelineCardAttributes, TimelineCardEditorIds, 'timeline-card'>;

type TimelinePeriod = {
  fromYear: string;
  tillYear?: string;
  sections: TimelineCardSection[];
  editorIds?: TimelinePeriodEditorIds;
};

type TimelineSection = Section<
  {
    numColumns: 12;
    headline?: string;
    periods: TimelinePeriod[];
  } & SectionAttributes,
  [
    'headline',
    'periods',
  ],
  'timeline'
>;

@Component({
    selector: 'section[app-timeline]',
    styleUrls: ['./timeline.component.scss'],
    templateUrl: './timeline.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule, EditorIdDirective, PlainImageComponent, RouterLink,
        CardComponent,
        ChipSetComponent,
        ChipComponent,
        HtmlComponent,
        FsButtonComponent,
    ],
})
export class TimelineComponent extends SectionComponent<TimelineSection> implements AfterViewInit, OnDestroy {

  private isServer = isPlatformServer(inject(PLATFORM_ID));
  private destroyRef = inject(DestroyRef);

  @ViewChildren('periodsContainerElement') private periodsContainerElements!: QueryList<ElementRef<HTMLElement>>;

  public override ngAfterViewInit(): void {
    this.setupAnimationTrigger();
  }

  private observer!: IntersectionObserver;

  private setupAnimationTrigger(): void {
    if (this.isServer) {
      return;
    }

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const classes = ['animate'];
          if (entry.intersectionRect.top === 0) {
            classes.push('animate-reverse');
          }
          entry.target.classList.add(...classes);
        } else {
          entry.target.classList.remove('animate', 'animate-reverse');
        }
      })
    });

    this.periodsContainerElements.changes.pipe(
      startWith(this.periodsContainerElements),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe((periodContainerElements: typeof this.periodsContainerElements) => {
      this.observer.disconnect();
      periodContainerElements.forEach((periodContainerElement) => {
        this.observer.observe(periodContainerElement.nativeElement);
      });
    });
  }

  public ngOnDestroy(): void {
    if (this.isServer) {
      return;
    }

    this.observer.disconnect();
  }

  protected getPeriodAnchor(period: TimelinePeriod): string {
    return `${this.anchor  }-${  period.fromYear  }-${  period.tillYear}`;
  }

}
